// apps/provider/src/services/api/googleCalendar.js
// Google Calendar integration methods
//
// Methods: initiateGoogleCalendarAuth, completeGoogleCalendarAuth,
//          getGoogleCalendarStatus, disconnectGoogleCalendar
//
// Response types: @prism-ehr/shared → GoogleCalendarInitiateAuthResponse,
//                 GoogleCalendarCompleteAuthResponse, GoogleCalendarStatus,
//                 GoogleCalendarDisconnectResponse

/**
 * @returns {Object} Google Calendar domain methods (bound to `this` of the composed apiService)
 */
export function createGoogleCalendarMethods() {
  return {
    /**
     * Initiate Google Calendar OAuth2 flow.
     * Returns an authorization URL to redirect the provider to Google consent.
     *
     * Xano: POST /google-calendar/initiate-auth
     * Xano file: 1135_google-calendar_initiate-auth_POST.xs
     *
     * @param {string} redirectUri - The OAuth callback URL
     * @returns {Promise<import('@prism-ehr/shared').GoogleCalendarInitiateAuthResponse>}
     */
    async initiateGoogleCalendarAuth(redirectUri) {
      return this.request('/google-calendar/initiate-auth', {
        method: 'POST',
        body: JSON.stringify({ redirect_uri: redirectUri })
      });
    },

    /**
     * Complete Google Calendar OAuth2 flow.
     * Exchanges the authorization code for tokens and stores the connection.
     *
     * Xano: POST /google-calendar/complete-auth
     * Xano file: 1136_google-calendar_complete-auth_POST.xs
     *
     * @param {string} code - Authorization code from Google
     * @param {string} state - CSRF state token
     * @param {string} redirectUri - The same redirect URI used in initiation
     * @returns {Promise<import('@prism-ehr/shared').GoogleCalendarCompleteAuthResponse>}
     */
    async completeGoogleCalendarAuth(code, state, redirectUri) {
      return this.request('/google-calendar/complete-auth', {
        method: 'POST',
        body: JSON.stringify({ code, state, redirect_uri: redirectUri })
      });
    },

    /**
     * Get Google Calendar connection status for the authenticated user.
     *
     * Xano: GET /google-calendar/status
     * Xano file: 1137_google-calendar_status_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').GoogleCalendarStatus>}
     */
    async getGoogleCalendarStatus() {
      return this.request('/google-calendar/status', {
        method: 'GET'
      });
    },

    /**
     * Disconnect Google Calendar for the authenticated user.
     * Revokes the OAuth token and clears the connection.
     *
     * Xano: POST /google-calendar/disconnect
     * Xano file: 1138_google-calendar_disconnect_POST.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').GoogleCalendarDisconnectResponse>}
     */
    async disconnectGoogleCalendar() {
      return this.request('/google-calendar/disconnect', {
        method: 'POST'
      });
    },
  };
}
